import { useState } from "react";
import { assetService } from "../api/assetService";
import { useToast } from "../context/ToastContext";
import { Asset, PaginationInfo } from "../models/asset.types";
// import { assetService } from "../services/assetService";
// import { Asset, PaginationInfo } from "../types/asset.types";
// import { useToast } from "../contexts/ToastContext";

export const useAssets = () => {
  const [assets, setAssets] = useState<Asset[]>([]);
  const [asset, setAsset] = useState<Asset | null>(null);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState<PaginationInfo>({
    page: 1,
    limit: 100,
    total: 0,
    total_pages: 0,
  });
  const [searchQuery, setSearchQuery] = useState<string>("");
  const { showSuccess, showError } = useToast();

  const fetchAssets = async (
    page: number = 1,
    limit: number = 100,
    search?: string
  ) => {
    setLoading(true);
    try {
      const response = await assetService.getAll(page, limit, search);
      setAssets(response.assets);
      setPagination(response.pagination);
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Failed to fetch assets";
      showError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const searchAssets = async (
    query: string,
    page: number = 1,
    limit: number = 100
  ) => {
    setLoading(true);
    setSearchQuery(query);
    try {
      const response = await assetService.search(query, page, limit);
      setAssets(response);
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Failed to search assets";
      showError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const fetchAsset = async (asset_id: string) => {
    if (!asset_id) return;
    setLoading(true);
    try {
      const asset = await assetService.getById(asset_id);
      setAsset(asset);
    } catch (err) {
      console.error(err.response);
      const errorMessage =
        err instanceof Error ? err.message : "Failed to fetch asset";
      showError(errorMessage);
      setAsset(null);
    } finally {
      setLoading(false);
    }
  };

  const createAsset = async (asset: Asset) => {
    setLoading(true);
    let resp = false;
    try {
      const data = await assetService.create(asset);
      if (data) {
        setAssets([...assets, data]);
        showSuccess("Asset created successfully");
        resp = true;
      } else {
        showError("Failed to create asset");
      }
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Failed to create asset";
      showError(errorMessage);
    } finally {
      setLoading(false);
      return resp;
    }
  };

  const updateAsset = async (id: string | number, asset: Partial<Asset>) => {
    setLoading(true);
    try {
      const data = await assetService.update(id, asset);
      if (data) {
        setAssets(assets.map((a) => (a.id === id ? data : a)));
        showSuccess("Asset updated successfully");
      } else {
        showError("Failed to update asset");
      }
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Failed to update asset";
      showError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const deleteAsset = async (id: string | number) => {
    setLoading(true);
    try {
      const success = await assetService.delete(id);
      if (success) {
        setAssets(assets.filter((a) => a.asset_id !== id && a.id !== id));
        showSuccess("Asset deleted successfully");
      } else {
        showError("Failed to delete asset");
      }
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Failed to delete asset";
      showError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const totalInventoryValue = () => {
    return assets.reduce((sum: number, asset: Asset) => {
      return sum + (asset.value || 0);
    }, 0);
  };

  const calculateDepreciation = (unit_value: number, life_span: number) => {
    return unit_value / life_span;
  };

  return {
    assets,
    asset,
    loading,
    pagination,
    searchQuery,
    fetchAssets,
    searchAssets,
    fetchAsset,
    createAsset,
    updateAsset,
    deleteAsset,
    totalInventoryValue,
    calculateDepreciation,
  };
};
